import { useCallback, useMemo, useState } from 'react';
import {
    DeliveryBoardEtaFilter,
    DeliveryBoardGrouping,
    DeliveryBoardSort,
    DeliveryBoardView,
} from './useDeliveryBoardFeed';
import { SettingsColumnDrawerRow } from '../pages/reporting/components/SettingsColumnsPanel';

export type DeliveryBoardColumnKey =
    | 'roNumber'
    | 'customerName'
    | 'vehicle'
    | 'vin'
    | 'timeStarted'
    | 'timePlaced'
    | 'description'
    | 'partNumber'
    | 'vendorName'
    | 'eta'
    | 'listPrice'
    | 'buyPrice'
    | 'grossMargin'
    | 'vendorPhone'
    | 'vendorEmail';

export type DeliveryBoardColumnPreset = 'default' | 'not-ordered';

interface StoredColumnState {
    key: DeliveryBoardColumnKey;
    visible: boolean;
}

export interface DeliveryBoardColumnDefinition {
    key: DeliveryBoardColumnKey;
    label: string;
    visible: boolean;
    pinned?: boolean;
}

const COLUMN_STORAGE_KEYS: Record<DeliveryBoardColumnPreset, string> = {
    default: 'delivery_board_columns',
    'not-ordered': 'delivery_board_columns_not_ordered',
};
const VIEW_STORAGE_KEY = 'delivery_board_view';
const GROUPING_STORAGE_KEY = 'delivery_board_grouping';
const SORT_STORAGE_KEY = 'delivery_board_sort';
const ETA_FILTER_STORAGE_KEY = 'delivery_board_eta_filters';

export const DEFAULT_DELIVERY_BOARD_COLUMNS: DeliveryBoardColumnDefinition[] = [
    { key: 'roNumber', label: 'RO Number', visible: true },
    { key: 'vendorName', label: 'Vendor Name', visible: true },
    { key: 'description', label: 'Part Name', visible: true },
    { key: 'partNumber', label: 'Part Number', visible: true },
    { key: 'eta', label: 'ETA', visible: true },
    { key: 'vendorPhone', label: 'Phone Number', visible: true },
    { key: 'customerName', label: 'Customer', visible: false },
    { key: 'vehicle', label: 'Vehicle', visible: false },
    { key: 'vin', label: 'VIN', visible: false },
    { key: 'timeStarted', label: 'Time Started', visible: false },
    { key: 'timePlaced', label: 'Time Placed', visible: false },
    { key: 'listPrice', label: 'List Price', visible: false },
    { key: 'buyPrice', label: 'Buy Price', visible: false },
    { key: 'grossMargin', label: 'Gross Margin', visible: false },
    { key: 'vendorEmail', label: 'Email', visible: false },
];

const NOT_ORDERED_DEFAULT_VISIBLE_ORDER: DeliveryBoardColumnKey[] = [
    'roNumber',
    'vehicle',
    'customerName',
    'description',
    'partNumber',
    'timeStarted',
];

export const DEFAULT_DELIVERY_BOARD_COLUMNS_NOT_ORDERED: DeliveryBoardColumnDefinition[] = (() => {
    const labelByKey = new Map(DEFAULT_DELIVERY_BOARD_COLUMNS.map((column) => [column.key, column.label]));
    const visibleSet = new Set(NOT_ORDERED_DEFAULT_VISIBLE_ORDER);

    const visibleColumns = NOT_ORDERED_DEFAULT_VISIBLE_ORDER.map((key) => ({
        key,
        label: labelByKey.get(key) as string,
        visible: true,
    }));
    const hiddenColumns = DEFAULT_DELIVERY_BOARD_COLUMNS
        .filter((column) => !visibleSet.has(column.key))
        .map((column) => ({ key: column.key, label: column.label, visible: false }));

    return [...visibleColumns, ...hiddenColumns];
})();

const DEFAULTS_BY_PRESET: Record<DeliveryBoardColumnPreset, DeliveryBoardColumnDefinition[]> = {
    default: DEFAULT_DELIVERY_BOARD_COLUMNS,
    'not-ordered': DEFAULT_DELIVERY_BOARD_COLUMNS_NOT_ORDERED,
};

export const DEFAULT_DELIVERY_BOARD_VIEW: DeliveryBoardView = 'list';
export const DEFAULT_DELIVERY_BOARD_GROUPING: DeliveryBoardGrouping = 'none';
export const DEFAULT_DELIVERY_BOARD_SORT: DeliveryBoardSort = { key: 'eta', dir: 'desc' };
export const DEFAULT_DELIVERY_BOARD_ETA_FILTERS: DeliveryBoardEtaFilter[] = ['today'];

const loadFromStorage = <T,>(key: string, fallback: T): T => {
    try {
        const stored = localStorage.getItem(key);
        return stored ? JSON.parse(stored) as T : fallback;
    } catch {
        return fallback;
    }
};

const saveToStorage = <T,>(key: string, value: T) => {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // ignore
    }
};

const loadColumnsForPreset = (preset: DeliveryBoardColumnPreset): DeliveryBoardColumnDefinition[] => {
    const defaults = DEFAULTS_BY_PRESET[preset];
    const stored = loadFromStorage<StoredColumnState[]>(COLUMN_STORAGE_KEYS[preset], []);
    if (stored.length === 0) return defaults;

    const visibleByKey = new Map(stored.map((entry) => [entry.key, entry.visible]));
    const storedOrder = stored.map((entry) => entry.key);
    const orderedKeys = [
        ...storedOrder.filter((key) => defaults.some((column) => column.key === key)),
        ...defaults.map((column) => column.key).filter((key) => !storedOrder.includes(key)),
    ];

    return orderedKeys.map((key) => {
        const fallback = defaults.find((column) => column.key === key)!;
        return {
            ...fallback,
            visible: visibleByKey.get(key) ?? fallback.visible,
        };
    });
};

const loadAllColumns = (): Record<DeliveryBoardColumnPreset, DeliveryBoardColumnDefinition[]> => ({
    default: loadColumnsForPreset('default'),
    'not-ordered': loadColumnsForPreset('not-ordered'),
});

export const useDeliveryBoardPreferences = (columnPreset: DeliveryBoardColumnPreset = 'default') => {
    const [columnsByPreset, setColumnsByPreset] = useState<Record<DeliveryBoardColumnPreset, DeliveryBoardColumnDefinition[]>>(loadAllColumns);
    const [view, setViewState] = useState<DeliveryBoardView>(() =>
        loadFromStorage(VIEW_STORAGE_KEY, DEFAULT_DELIVERY_BOARD_VIEW)
    );
    const [grouping, setGroupingState] = useState<DeliveryBoardGrouping>(() => {
        const stored = loadFromStorage<DeliveryBoardGrouping>(GROUPING_STORAGE_KEY, DEFAULT_DELIVERY_BOARD_GROUPING);
        // Migrate the retired 'order' grouping to the supplier grouping that replaced it.
        if ((stored as string) === 'order') return 'supplier';
        return stored === 'ro' || stored === 'supplier' || stored === 'none' ? stored : DEFAULT_DELIVERY_BOARD_GROUPING;
    });
    const [sort, setSortState] = useState<DeliveryBoardSort>(() =>
        loadFromStorage(SORT_STORAGE_KEY, DEFAULT_DELIVERY_BOARD_SORT)
    );
    const [etaFilters, setEtaFiltersState] = useState<DeliveryBoardEtaFilter[]>(() =>
        loadFromStorage(ETA_FILTER_STORAGE_KEY, DEFAULT_DELIVERY_BOARD_ETA_FILTERS)
    );

    const columns = columnsByPreset[columnPreset];

    const setView = useCallback((next: DeliveryBoardView) => {
        setViewState(next);
        saveToStorage(VIEW_STORAGE_KEY, next);
    }, []);

    const setGrouping = useCallback((next: DeliveryBoardGrouping) => {
        setGroupingState(next);
        saveToStorage(GROUPING_STORAGE_KEY, next);
    }, []);

    const setSort = useCallback((next: DeliveryBoardSort) => {
        setSortState(next);
        saveToStorage(SORT_STORAGE_KEY, next);
    }, []);

    const setEtaFilters = useCallback((next: DeliveryBoardEtaFilter[]) => {
        setEtaFiltersState(next);
        saveToStorage(ETA_FILTER_STORAGE_KEY, next);
    }, []);

    const persistColumns = useCallback((next: DeliveryBoardColumnDefinition[]) => {
        setColumnsByPreset((current) => ({
            ...current,
            [columnPreset]: next,
        }));
        saveToStorage(COLUMN_STORAGE_KEYS[columnPreset], next.map((column) => ({
            key: column.key,
            visible: column.visible,
        })));
    }, [columnPreset]);

    const toggleColumn = useCallback((key: DeliveryBoardColumnKey) => {
        persistColumns(columns.map((column) => (
            column.key === key ? { ...column, visible: !column.visible } : column
        )));
    }, [columns, persistColumns]);

    const reorderColumns = useCallback((fromKey: string, toKey: string) => {
        const next = [...columns];
        const fromIndex = next.findIndex((column) => column.key === fromKey);
        const toIndex = next.findIndex((column) => column.key === toKey);
        if (fromIndex === -1 || toIndex === -1) return;

        const [removed] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, removed);
        persistColumns(next);
    }, [columns, persistColumns]);

    const resetColumns = useCallback(() => {
        persistColumns(DEFAULTS_BY_PRESET[columnPreset]);
    }, [columnPreset, persistColumns]);

    const columnRows = useMemo<SettingsColumnDrawerRow[]>(() => (
        columns.map((column) => ({
            key: column.key,
            label: column.label,
            visible: column.visible,
            pinned: !!column.pinned,
        }))
    ), [columns]);

    const visibleColumnKeys = useMemo(
        () => columns.filter((column) => column.visible).map((column) => column.key),
        [columns]
    );

    return {
        columns,
        columnRows,
        visibleColumnKeys,
        toggleColumn,
        reorderColumns,
        resetColumns,
        view,
        setView,
        grouping,
        setGrouping,
        sort,
        setSort,
        etaFilters,
        setEtaFilters,
    };
};
